import { Popover as BasePopover } from '@base-ui/react/popover'
import React, { useState } from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { DEFAULT_Z_INDEX, Popover } from 'sutra/components/Popover/Popover'
import { semantic } from 'sutra/tokens/colors'

export type PopconfirmClickEvent = React.MouseEvent<HTMLButtonElement>

export interface PopconfirmOkButtonProps {
  // Solid red OK button (antd's `danger` on a primary button).
  danger?: boolean
  // Spinner on OK. Also on while an async `onConfirm` is pending.
  loading?: boolean
  disabled?: boolean
}

/**
 * A small anchored confirmation, replacing antd `Popconfirm`. Built on the
 * Sutra `Popover` (not a Base UI `AlertDialog`, which is a centred modal with
 * no anchor). `children` IS the trigger, with no wrapper element.
 *
 * ```tsx
 * <Popconfirm
 *   title="Delete this product?"
 *   okText="Delete"
 *   okButtonProps={{ danger: true }}
 *   onConfirm={() => deleteProduct(id)}
 * >
 *   <Button variant="secondary">Delete</Button>
 * </Popconfirm>
 * ```
 *
 * ## Confirming
 * OK calls `onConfirm(event)` and then closes. If `onConfirm` returns a
 * promise the OK button shows a spinner and Cancel is disabled until it
 * settles; the popup closes only if it resolves. A rejection keeps the popup
 * open (so the user can retry or cancel) and is re-reported with
 * `reportError`, never swallowed. A synchronous throw likewise keeps it open.
 *
 * ## Cancelling
 * Cancel, Escape and a press outside all call `onCancel` and close. While an
 * async `onConfirm` is pending none of them closes it.
 *
 * ## Mapping from antd
 * - `placement` -> `side` (+ `align`: `topLeft` is `side="top" align="start"`).
 * - `okButtonProps` / `okText` / `cancelText` / `title` / `description` /
 *   `open` / `onOpenChange` / `disabled` / `onConfirm` / `onCancel`: same
 *   names. `onOpenChange` fires on every change, including a close from OK or
 *   Cancel (as antd's did), and `open` makes the popup controlled.
 * - `overlayStyle={{ zIndex }}` -> `zIndex`.
 * - NOT supported (no call site used them): `icon`, `showCancel`,
 *   `cancelButtonProps`, `getPopupContainer`, `trigger`.
 */
export interface PopconfirmProps {
  // The element the popup anchors to. It becomes the trigger itself, so it
  // must accept a ref and spread unknown props onto its DOM node.
  children: React.ReactElement
  // Also the dialog's accessible name.
  title: React.ReactNode
  description?: React.ReactNode
  okText?: React.ReactNode
  cancelText?: React.ReactNode
  okButtonProps?: PopconfirmOkButtonProps
  // May return a promise; see "Confirming".
  onConfirm?: (event: PopconfirmClickEvent) => unknown
  onCancel?: () => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  // An inert trigger: the popup never opens.
  disabled?: boolean
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  // Set to false when `children` renders a non-<button> (a span, a div).
  nativeButton?: boolean
  zIndex?: number
}

const StyledPopover = styled(Popover)`
  max-width: 320px;
`

const Description = styled(BasePopover.Description)`
  margin: 0;
  color: ${semantic.fgMuted};
  font-size: 14px;
  line-height: 20px;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`

const isPromiseLike = (value: unknown): value is PromiseLike<unknown> =>
  typeof (value as { then?: unknown } | null)?.then === 'function'

export const Popconfirm = ({
  children,
  title,
  description,
  okText = 'OK',
  cancelText = 'Cancel',
  okButtonProps,
  onConfirm,
  onCancel,
  open: openProp,
  onOpenChange,
  disabled = false,
  side = 'top',
  align = 'center',
  nativeButton = true,
  zIndex = DEFAULT_Z_INDEX,
}: PopconfirmProps): React.ReactElement => {
  const [innerOpen, setInnerOpen] = useState(false)
  const [pending, setPending] = useState(false)

  const open = !disabled && (openProp ?? innerOpen)

  const setOpen = (next: boolean): void => {
    if (openProp === undefined) setInnerOpen(next)
    onOpenChange?.(next)
  }

  // Every close that Base UI starts (Escape, an outside press, a second click
  // on the trigger) is a cancel; OK and Cancel close through `setOpen` directly.
  const handlePopoverOpenChange = (next: boolean): void => {
    if (disabled || pending) return
    if (!next) onCancel?.()
    setOpen(next)
  }

  const handleCancel = (): void => {
    onCancel?.()
    setOpen(false)
  }

  const handleConfirm = async (event: PopconfirmClickEvent): Promise<void> => {
    const result = onConfirm?.(event)
    if (isPromiseLike(result)) {
      setPending(true)
      try {
        await result
      } catch (error) {
        setPending(false)
        reportError(error)
        return
      }
      setPending(false)
    }
    setOpen(false)
  }

  return (
    <StyledPopover
      open={open}
      onOpenChange={handlePopoverOpenChange}
      title={title}
      side={side}
      align={align}
      nativeButton={nativeButton}
      zIndex={zIndex}
      content={
        <>
          {description != null && <Description>{description}</Description>}
          <Actions>
            <Button
              size="xs"
              variant="secondary"
              disabled={pending}
              onClick={handleCancel}
            >
              {cancelText}
            </Button>
            <Button
              size="xs"
              variant={okButtonProps?.danger ? 'destructive' : 'primary'}
              loading={pending || okButtonProps?.loading}
              disabled={okButtonProps?.disabled}
              onClick={handleConfirm}
            >
              {okText}
            </Button>
          </Actions>
        </>
      }
    >
      {children}
    </StyledPopover>
  )
}

Popconfirm.displayName = 'Popconfirm'
